/**
 * @param {Array} data - fish rows with .length, .relativeWeight, .status
 * @param {{ unitValue: string, binSizeValue?: number }} opts - English uses inch bins; Metric uses mm
 * @param {number} minLength - global min length for bin ladder
 * @param {number} maxLength - global max length for bin ladder
 */
export function processBinDataWithRange(
  data,
  { unitValue, binSizeValue },
  minLength,
  maxLength
) {
  if (!Number.isFinite(minLength) || !Number.isFinite(maxLength)) return [];
  if (minLength > maxLength) return [];

  const binWidth = binSizeValue ?? (unitValue === "English" ? 1 : 10);
  const bins = [];

  let currentStart = Math.floor(minLength / binWidth) * binWidth;

  while (currentStart <= maxLength) {
    const currentEnd = currentStart + binWidth;

    bins.push({
      min: currentStart,
      max: currentEnd,
      label:
        unitValue === "English"
          ? `${currentStart.toFixed(1)}-${currentEnd.toFixed(1)}`
          : `${Math.round(currentStart)}-${Math.round(currentEnd - 1)}`,
      number: 0,
      culled: 0,
      stocking: 0,
      released: 0,
      totalWr: 0,
      wrSquared: 0,
      fishCount: 0,
    });
    currentStart = currentEnd;
  }

  const rows = data || [];
  rows.forEach((fish) => {
    const length = fish.length;
    const bin = bins.find((b, index) => {
      if (index === bins.length - 1) {
        return length >= b.min && length <= b.max;
      }
      return length >= b.min && length < b.max;
    });

    if (bin) {
      const head = Math.max(
        1,
        Math.floor(Number(fish.stockingHeadcount) || 1)
      );
      bin.number += head;
      const status = (fish.status || "").toLowerCase();
      if (fish.isStocking || status === "stocking") {
        bin.stocking += head;
      } else if (status === "culled" || status === "cull") {
        bin.culled += head;
      }
      const wr = Number(fish.relativeWeight);
      if (Number.isFinite(wr) && head === 1) {
        bin.fishCount += 1;
        bin.totalWr += wr;
        bin.wrSquared += Math.pow(wr, 2);
      } else if (Number.isFinite(wr) && head > 1) {
        bin.fishCount += head;
        bin.totalWr += wr * head;
        bin.wrSquared += Math.pow(wr, 2) * head;
      }
    }
  });

  bins.forEach((bin) => {
    bin.released = bin.number - bin.culled - bin.stocking;
  });

  return bins.map((bin) => ({
    binLabel: bin.label,
    number: bin.number,
    culled: bin.culled,
    stocking: bin.stocking,
    released: bin.released,
    averageWr: bin.fishCount > 0 ? bin.totalWr / bin.fishCount : 0,
    stdev:
      bin.fishCount > 1
        ? Math.sqrt(
            (bin.wrSquared - Math.pow(bin.totalWr, 2) / bin.fishCount) /
              (bin.fishCount - 1)
          )
        : 0,
  }));
}

/**
 * @param {Array} data - fish rows with .length, .relativeWeight, .status
 * @param {{ unitValue: string, binSizeValue?: number }} opts - English uses inch bins; Metric uses mm
 */
export function processBinData(data, opts) {
  if (!data.length) return [];
  const lengths = data.map((fish) => fish.length);
  const minLength = Math.min(...lengths);
  const maxLength = Math.max(...lengths);
  return processBinDataWithRange(data, opts, minLength, maxLength);
}
